import React, { useState, useEffect, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, Plus, Printer, Download, Loader2, Trash2, Send } from 'lucide-react';
import api from '../api/axios';
import { useProfile } from '../contexts/ProfileContext';
import { useNavigate } from 'react-router-dom';
import { escapeCsvCell } from '../utils/csv';

const PatientManagementApp = () => {
  const [currentPage, setCurrentPage] = useState('list');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [consentChecked, setConsentChecked] = useState(true);
  const [previewImages, setPreviewImages] = useState(null);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [reportLanguage, setReportLanguage] = useState('en');
  const { profile ,loading} = useProfile();
  
  // Form state for new/edit patient
  const [formData, setFormData] = useState({
    name: '',
    patientId: '',
    gender: 'Male',
    ethnicity: '',
    dob: '',
    email: '',
    diabetes: '',
    hypertension: '',
    highCholesterol: '',
  });

  const [totalRegisteredPatientsCount, setTotalRegisteredPatientsCount] = useState(0);
  const [newPatientsTodayCount, setNewPatientsTodayCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [paginationPage, setPaginationPage] = useState(1);
  const [totalPatients, setTotalPatients] = useState(0);
  const [allPatients, setAllPatients] = useState([]);
  const pageSize = 10;
  const [error, setError] = useState(null);
  const [reportError, setReportError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'patientId', direction: 'ascending' });

  const [visualSymptoms, setVisualSymptoms] = useState({
    visualSymptoms: false,
    recentChangeInVision: false,
    ocularDiscomfort: false,
    otherVisualOrOcularConcern: false,
  });
  const [savedSymptoms, setSavedSymptoms] = useState(null);
  const [symptomsHistory, setSymptomsHistory] = useState([]);
  const [isReferralLoading, setIsReferralLoading] = useState(false);
  const [hasViewedReport, setHasViewedReport] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(null);
  const [isSendingReport, setIsSendingReport] = useState(false);

  //const navigate = useNavigate();

  function utcToLocal(utcStr) {
  try {
    if (typeof utcStr !== 'string') throw new Error("Input must be a string");

    // Trim to milliseconds (JavaScript only supports 3 digits)
    let trimmed = utcStr.includes('.') ? utcStr.slice(0, utcStr.indexOf('.') + 4) : utcStr;

    // Add 'Z' to mark it as UTC
    let date = new Date(trimmed + "Z");

    // Check if date is valid
    if (isNaN(date.getTime())) throw new Error("Invalid UTC date string");

    return date.toLocaleString(); // local date/time string
  } catch (err) {
    console.error("Error converting UTC to local:", err.message);
    return null; // or return a default/fallback value
  }
}

  const handleDeleteImage = async (eye) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await api.get(`webapi/deleteimage?eye=${eye}`);
      setPreviewImages(prev => {
        if (!prev) return prev;
        const newImages = { ...prev };
        if (eye === 'r') delete newImages.right;
        if (eye === 'l') delete newImages.left;
        return newImages;
      });
      setSuccessMessage('Image deleted successfully.');
    } catch (error) {
      console.error('Error deleting image:', error);
      setError('Failed to delete image.');
    }
  };

  useEffect(() => {

    // if (!loading && profile) {
    //   console.log('Profile loaded in PatientManagement:', profile);
    //   if (profile.userGroup !== 'DEVICE' && profile.userGroup !== 'OPERATOR')
    //       navigate('/dashboard', { replace: true });
    // }

    let intervalId = null;

    if (currentPage === 'detail' && selectedPatient && profile?.userGroup === 'DEVICE') {
      let lastImgLeftFname = null;
      let lastImgRightFname = null;

      const fetchPreviewImages = async () => {
        try {
          setReportError(null);
          const params = new URLSearchParams();
          //params.append("DevID","DEMO001")
          const response = await api.post("webapi/imgPreview", params);
          const data = response.data;
          if (data && data.images) {
            setPreviewImages(data.images);
          }
        } catch (error) {
          console.error("Error fetching preview images:", error);
          setReportError("fetching preview images");
          lastImgLeftFname = null;
          lastImgRightFname = null;
        }
      };

      const fetchPhotoData = async () => {
        try {
          const response = await api.get(`webapi/photo`);
          const timeStampElement = document.getElementById('TimeStamp');
          if (timeStampElement) {
            timeStampElement.innerHTML = response.data.msg || '';
          }
          
          const { imgLeftFname, imgRightFname } = response.data;
          if (imgLeftFname !== lastImgLeftFname || imgRightFname !== lastImgRightFname) {
            lastImgLeftFname = imgLeftFname;
            lastImgRightFname = imgRightFname;
            fetchPreviewImages();
            setHasViewedReport(false);
            setReportSuccess(null);
            setReportError(null);
          }
          //setReportError(null);
        } catch (error) {
          console.error('Error fetching photo data:', error);
          setReportError("fetching photo data")
        }
      };

      // Fetch data immediately when the component mounts or page changes to 'detail'
      fetchPhotoData();

      // Then set up the interval to fetch data every 2 seconds
      intervalId = setInterval(() => { fetchPhotoData(); }, 5000); // Corrected to 5 seconds
    }

    return () => clearInterval(intervalId); // Cleanup: clear interval when the component unmounts or dependencies change.
  }, [currentPage, selectedPatient, profile]);

  // Callback to fetch dashboard statistics
  const fetchDashboardStats = useCallback(async () => {
    console.log("Fetching dashboard statistics...");
    try {
      const response = await api.get('webapi/patients?stats');
      if (response.data) {
        setTotalRegisteredPatientsCount(response.data.totalRegisteredPatients || 0);
        setNewPatientsTodayCount(response.data.newlyRegisteredToday || 0);
        // The userGroup is now available from the profile context, so no need to set it here.
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    }
  }, []);

  const fetchAllPatients = useCallback(async () => {
    console.log("Fetching all patients...");
    try {
      const response = await api.get('webapi/patients');
      setAllPatients(response.data || []);
      setTotalPatients(response.data.length || 0);
      setHasSearched(true); // Show table with all patients
    } catch (error) {
      console.error("Error fetching all patients:", error);
      setError('Failed to fetch patient list.');
    }
  }, []);

  useEffect(() => {
    if (currentPage === 'list') {
      fetchDashboardStats();
      //fetchAllPatients();
    }
  }, [currentPage, fetchDashboardStats, fetchAllPatients]);

  const fetchSymptoms = async (patientPk) => {
    try {
      const resp = await api.get(`/webapi/symptoms?patientPk=${patientPk}`);
      const { currentSession, symptoms } = resp.data || {};
      setSymptomsHistory(symptoms || []);
      if (currentSession) {
        setVisualSymptoms({
          visualSymptoms: currentSession.visualSymptoms,
          recentChangeInVision: currentSession.recentChangeInVision,
          ocularDiscomfort: currentSession.ocularDiscomfort,
          otherVisualOrOcularConcern: currentSession.otherVisualOrOcularConcern,
        });
        const hasAny = currentSession.visualSymptoms || currentSession.recentChangeInVision ||
                       currentSession.ocularDiscomfort || currentSession.otherVisualOrOcularConcern;
        setSavedSymptoms(hasAny ? currentSession : null);
      }
    } catch (e) {
      // No symptoms recorded yet
    }
  };

  const handleViewPatient = async (patient) => {
    setConsentChecked(true);
    setError(null);
    setReportError(null);
    setReportSuccess(null);
    setSuccessMessage(null);
    setHasViewedReport(false);
    setSelectedPatient(patient);
    setFormData({
      patientName: patient.patientName,
      patientId: patient.patientId,
      gender: patient.gender,
      ethnicity: patient.ethnicity,
      dob: patient.dob,
      email: patient.email || '',
      diabetes: patient.diabetes || '',
      hypertension: patient.hypertension || '',
      highCholesterol: patient.highCholesterol || '',
    });
    setVisualSymptoms({ visualSymptoms: false, recentChangeInVision: false, ocularDiscomfort: false, otherVisualOrOcularConcern: false });
    setSavedSymptoms(null);
    setSymptomsHistory([]);
    setCurrentPage('detail');
    fetchSymptoms(patient.patientPk);
  };

  const handleAddPatient = () => {
    setError(null);
    setReportError(null);
    setSuccessMessage(null);
    setHasViewedReport(false);
    setSelectedPatient(null);
    setFormData({
      patientName: '',
      patientId: '',
      gender: 'U',
      ethnicity: 'Unknown',
      dob: '',
      email: '',
      diabetes: '',
      hypertension: '',
      highCholesterol: '',
    });
    setVisualSymptoms({ visualSymptoms: false, recentChangeInVision: false, ocularDiscomfort: false, otherVisualOrOcularConcern: false });
    setSavedSymptoms(null);
    setSymptomsHistory([]);
    setCurrentPage('detail');
  };

  const handleSave = async () => {
    setError(null);
    setSuccessMessage(null);

    if (!formData.patientName || formData.patientName.trim() === '') {
      setError('Please give name.');
      return;
    }

    if (!formData.patientId || formData.patientId.trim() === '') {
      setError('Please give patient ID.');
      return;
    }
    if (!formData.ethnicity || formData.ethnicity.trim() === '') {
      setError('Please give ethnicity.');
      return;
    }

    // Verify DOB format
    const dobRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (formData.dob && !dobRegex.test(formData.dob)) {
      setError('Invalid DOB format. Please use YYYY-MM-DD.');
      return;
    }
    if (!formData.dob ) {
      setError('Please give DOB. use YYYY-MM-DD.');
      return;
    }

    try {
      let currentPatientPk;
      if (selectedPatient) {
        await api.put(`/webapi/patients`, { ...formData, patientPk: selectedPatient.patientPk });
        currentPatientPk = selectedPatient.patientPk;
        setSuccessMessage('Patient details updated successfully.');
      } else {
        const response = await api.post(`/webapi/patients`, formData);
        // After creating, transition to edit mode for the new patient
        setSelectedPatient(response.data);
        currentPatientPk = response.data.patientPk;
        setSuccessMessage('Patient added successfully.');
      }

      // Post symptoms if patient has existing symptoms or any are currently selected
      const hasAnySymptom = Object.values(visualSymptoms).some(v => v);
      const hadPreviousSymptoms = savedSymptoms && Object.values(savedSymptoms).some(v => v);
      if (hasAnySymptom || hadPreviousSymptoms) {
        await api.post('/webapi/symptoms', { sessionId: profile.sessionId, patientPk: currentPatientPk, ...visualSymptoms });
        await fetchSymptoms(currentPatientPk);
      }

      await fetchDashboardStats(); // Refresh dashboard stats after save

      // if (hasAnySymptom) {
      //   const confirmed = window.confirm('Do you need to generate referral letter now?');
      //   if (confirmed) {
      //     handleViewReport('referral');
      //   }
      // }
    } catch (error) {
      console.error('Error saving patient.');
      const message = selectedPatient ? 'Failed to update patient.' : 'Failed to add new patient.';
      setError(`${message} Please try again or contact support if the problem persists.`);
    }
  };

  const handleGenerateReferralLetter = () => {
    handleViewReport('referral');
  };

  const handleSendReport = async () => {
    if (!selectedPatient) return;
    setReportError(null);
    setReportSuccess(null);
    setIsSendingReport(true);
    try {
      await api.post('webapi/SendReportToPatient', {
        patientPk: selectedPatient.patientPk,
        lang: reportLanguage,
        consent: consentChecked ? 'yes' : 'no',
      });
      setReportSuccess('Report sent to patient successfully.');
    } catch (err) {
      console.error('Error sending report:', err);
      setReportError(err?.response?.data?.error || 'Failed to send report to patient.');
    } finally {
      setIsSendingReport(false);
    }
  };

  // const handleViewReport = async () => {
  //   if (selectedPatient) {
  //     setIsReportLoading(true);

  //     try {
  //       const response = await api.get(`webapi/PatientReportData?pk=${selectedPatient.patientPk}&consent=${consentChecked ? "yes" : "no"}&lang=${reportLanguage}`, {
  //         responseType: 'blob'
  //       });
  //       //throw new Error("Something went wrong");
        
  //       let filename = `Report_${selectedPatient.patientId}_${new Date().toISOString().split('T')[0]}.pdf`;
  //       const contentDisposition = response.headers['content-disposition'];
  //       if (contentDisposition) {
  //         const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  //         if (matches && matches[1]) {
  //           filename = matches[1].replace(/['"]/g, '');
  //         }
  //       }

  //       const blob = new Blob([response.data], { type: 'application/pdf' });
  //       const url = URL.createObjectURL(blob);
  //       const link = document.createElement('a');
  //       link.href = url;
  //       link.setAttribute('download', filename);
  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);
  //       URL.revokeObjectURL(url);
  //     } catch (err) {
  //       //setError('Failed to fetch report data. Please try again.');
  //       console.error('Error fetching report data:', err);
  //       setReportError('Failed to fetch report data. Please try again.');
  //     } finally {
  //       setIsReportLoading(false);
  //     }
  //   } else {
  //     //setError("No patient is selected to view a report.");
  //     setReportError("No patient is selected to view a report.");
  //   }
  // };

  const handleViewReport = async (type) => {
    if (selectedPatient) {
      setIsReportLoading(true);
      try {        

        const form = document.createElement('form');
        form.method = 'post';
        //form.action = 'https://RA30.SyrisAI.com/EyeAIpdfReport';
        form.action = 'webapi/PatientReportData';
        form.target = '_blank';

        // for (const key in reportData) {
        //   if (reportData.hasOwnProperty(key)) {
        //     const hiddenField = document.createElement('input');
        //     hiddenField.type = 'hidden';
        //     hiddenField.name = key;
        //     hiddenField.value = reportData[key];
        //     form.appendChild(hiddenField);
        //   }
        // }
        //for consent
        const hiddenFieldpk = document.createElement('input');
        hiddenFieldpk.type = 'hidden';
        hiddenFieldpk.name = "pk";
        hiddenFieldpk.value = selectedPatient.patientPk;
        form.appendChild(hiddenFieldpk);

        const hiddenFieldlang = document.createElement('input');
        hiddenFieldlang.type = 'hidden';
        hiddenFieldlang.name = "lang";
        hiddenFieldlang.value = reportLanguage;
        form.appendChild(hiddenFieldlang);

        const hiddenFieldtype = document.createElement('input');
        hiddenFieldtype.type = 'hidden';
        hiddenFieldtype.name = "type";
        hiddenFieldtype.value = type;
        form.appendChild(hiddenFieldtype);

        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = "Consent";
        hiddenField.value = consentChecked ? "yes" : "no";
        form.appendChild(hiddenField);

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
        setHasViewedReport(true);
      } catch (err) {
        setReportError('Failed to fetch report data. Please try again.');
        console.error('Error fetching report data:', err);
      }
      finally {
         setIsReportLoading(false);
         setReportSuccess(null);
         
       }
    } else {
      setReportError("No patient is selected to view a report.");
    }
  }
    
  const handleBack = () => {
    setError(null);
    setSearchKeyword('');
    setPatients([]);
    setTotalPatients(0);
    setPaginationPage(1);
    setHasSearched(false);
    setCurrentPage('list');
    setPreviewImages(null);
    setSymptomsHistory([]);
    setSavedSymptoms(null);
  };

  const handleSearch = async (event) => {
    if (event.key !== 'Enter' ) {
      return;
    }

    //setHasSearched(true);
    setPaginationPage(1);

    if (searchKeyword.trim() === '') {
      await fetchAllPatients();
      setHasSearched(true); // Ensure table is shown
      return;
    }

    if (searchKeyword.length < 3) {
      // If keyword is not empty but too short, do nothing
      return;
    }

    setHasSearched(true);

    try {
      if (searchKeyword.trim() === '') {
        await fetchAllPatients();
      } else {
        const response = await api.get(`webapi/patients?search=${searchKeyword}`);
        const fetchedPatients = response.data || [];
        setAllPatients(fetchedPatients);
        setTotalPatients(fetchedPatients.length);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
      setAllPatients([]);
      setTotalPatients(0);
    }
  };

  const handleDownloadCSV = () => {
    if (!allPatients || allPatients.length === 0) {
      setError('No patients to download.');
      return;
    }

    const headers = ['Patient ID', 'Name', 'Gender', 'Ethnicity', 'DOB'];
    const csvContent = [
      headers.join(','),
      ...allPatients.map(patient => {
        return [
          escapeCsvCell(patient.patientId),
          escapeCsvCell(patient.patientName),
          escapeCsvCell(patient.gender),
          escapeCsvCell(patient.ethnicity),
          escapeCsvCell(patient.dob)
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `patients_list_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sortedPatients = React.useMemo(() => {
    let sortablePatients = [...allPatients];
    if (sortConfig !== null) {
      sortablePatients.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortablePatients;
  }, [allPatients, sortConfig]);

  const requestSort = (uiKey, dataKey) => {
    let direction = 'ascending';
    if (sortConfig.key === (dataKey || uiKey) && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key: dataKey || uiKey, direction });
    setPaginationPage(1); // Reset to first page on new sort
  };

  const totalPages = Math.ceil(totalPatients / pageSize);

  // Patient List Page
  if (currentPage === 'list') {
    return (
      <div className="min-h-screen max-w-4xl ">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Patient list</h1>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-600 text-sm mb-2">Registered Patients</p>
            <p className="text-4xl font-bold text-gray-800">{totalRegisteredPatientsCount}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-600 text-sm mb-2">New Patients Today</p>
            <p className="text-4xl font-bold text-gray-800">{newPatientsTodayCount} <span className="text-xl text-gray-500">/ {totalRegisteredPatientsCount}</span></p>
          </div>
        </div>

        {/* Search and Add Button */}
        <div className="mb-6">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Enter keyword (min 3 chars) and press Enter"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={handleSearch}
              autoComplete="off"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleAddPatient}
              className="bg-indigo-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-indigo-700 transition"
            >
              <Plus size={20} />
              Patient
            </button>
            <button
              onClick={handleDownloadCSV}
              className="bg-green-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-green-700 transition"
            >
              <Download size={20} />
              CSV
            </button>
          </div>
        </div>

        {/* Patient Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200"> 
              <tr> 
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700 cursor-pointer" onClick={() => requestSort('patientId', 'patientId')}>
                  <div className="flex items-center gap-1">
                    Patient ID
                    <span className="flex flex-col">
                      <ChevronLeft size={12} className={`-mb-1.5 transform rotate-90 ${sortConfig.key === 'patientId' && sortConfig.direction === 'ascending' ? 'text-indigo-600' : 'text-gray-400'}`} />
                      <ChevronRight size={12} className={`transform rotate-90 ${sortConfig.key === 'patientId' && sortConfig.direction === 'descending' ? 'text-indigo-600' : 'text-gray-400'}`} />
                    </span>
                  </div>
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700 cursor-pointer" onClick={() => requestSort('patientName', 'patientName')}>
                  <div className="flex items-center gap-1">
                    Name
                    <span className="flex flex-col">
                      <ChevronLeft size={12} className={`-mb-1.5 transform rotate-90 ${sortConfig.key === 'patientName' && sortConfig.direction === 'ascending' ? 'text-indigo-600' : 'text-gray-400'}`} />
                      <ChevronRight size={12} className={`transform rotate-90 ${sortConfig.key === 'patientName' && sortConfig.direction === 'descending' ? 'text-indigo-600' : 'text-gray-400'}`} />
                    </span>
                  </div>
                </th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-700 cursor-pointer" onClick={() => requestSort('gender', 'gender')}>
                  <div className="flex items-center justify-end gap-1">
                    Gender
                    <span className="flex flex-col">
                      <ChevronLeft size={12} className={`-mb-1.5 transform rotate-90 ${sortConfig.key === 'gender' && sortConfig.direction === 'ascending' ? 'text-indigo-600' : 'text-gray-400'}`} />
                      <ChevronRight size={12} className={`transform rotate-90 ${sortConfig.key === 'gender' && sortConfig.direction === 'descending' ? 'text-indigo-600' : 'text-gray-400'}`} />
                    </span>
                  </div>
                </th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-700 cursor-pointer" onClick={() => requestSort('dateTimeCreated', 'dateTimeCreated')}>
                  <div className="flex items-center justify-end gap-1">
                    Date Created
                    <span className="flex flex-col">
                      <ChevronLeft size={12} className={`-mb-1.5 transform rotate-90 ${sortConfig.key === 'dateTimeCreated' && sortConfig.direction === 'ascending' ? 'text-indigo-600' : 'text-gray-400'}`} />
                      <ChevronRight size={12} className={`transform rotate-90 ${sortConfig.key === 'dateTimeCreated' && sortConfig.direction === 'descending' ? 'text-indigo-600' : 'text-gray-400'}`} />
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {!hasSearched ? (
                <tr>
                  <td colSpan="4" className="text-center py-12 text-red-500">
                    
                  </td>
                </tr>
              ) : totalPatients === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-12 text-gray-500">
                    No patients found matching your search.
                  </td>
                </tr>
              ) : (
                sortedPatients.slice((paginationPage - 1) * pageSize, paginationPage * pageSize).map((patient) => (
                  <tr 
                    key={patient.patientPk} 
                    className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleViewPatient(patient)}
                  >
                    <td className="px-6 py-4 text-gray-800">{patient.patientId}</td>
                    <td className="px-6 py-4 text-gray-800">{patient.patientName}</td>
                    <td className="px-6 py-4 text-gray-800 text-right">{patient.gender}</td>
                    <td className="px-6 py-4 text-gray-800 text-right whitespace-nowrap">{patient.dateTimeCreated ? utcToLocal(patient.dateTimeCreated) : ''}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {/* Pagination */}
          <div className="flex justify-end items-center gap-2 px-6 py-4 border-t border-gray-200">
            <span className="text-sm text-gray-600">
              Page {paginationPage} of {totalPages}
            </span>
            <button 
              onClick={() => setPaginationPage(p => p - 1)}
              disabled={paginationPage <= 1}
              className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={20} className="text-gray-600" />
            </button>
            <button 
              onClick={() => setPaginationPage(p => p + 1)}
              disabled={paginationPage >= totalPages}
              className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={20} className="text-gray-600" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Patient Detail Page
  return (
    <div className="min-h-screen max-w-6xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Patient</h1>
        <div className="flex items-center gap-2 text-sm">
          <span 
            className="text-indigo-600 cursor-pointer hover:underline"
            onClick={handleBack}
          >
            Patient list
          </span>
          <span className="text-gray-400">/</span>
          <span className="text-gray-600">Patient Record</span>
        </div>
      </div>

      {selectedPatient && (
        <>
          {/* Action Buttons */}
          
          {profile?.userGroup === 'DEVICE' && (
            <>
              {previewImages && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {previewImages.right ? (
                    <div className="bg-white rounded-lg shadow p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-semibold text-gray-800">Right Eye</h3>
                        <button
                          onClick={() => handleDeleteImage('r')}
                          className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded-full transition-colors"
                          title="Delete Right Eye Image"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                      <img
                        src={`data:image/${previewImages.right.format};base64,${previewImages.right.base64_data}`}
                        alt="Right Eye"
                        className="w-full h-auto rounded-lg"
                      />
                      <div className="mt-2 text-xs text-gray-500 flex justify-between">
                        <span>{previewImages.right.width}x{previewImages.right.height}</span>
                        <span>{(previewImages.right.size_bytes / 1024).toFixed(1)} KB</span>
                        <span>{utcToLocal(previewImages.right.last_modified)}</span>
                        
                      </div>                     
                    </div>
                  ) : (
                    <div></div>
                  )}
                  
                  {previewImages.left && (
                    <div className="bg-white rounded-lg shadow p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-semibold text-gray-800">Left Eye</h3>
                        <button
                          onClick={() => handleDeleteImage('l')}
                          className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded-full transition-colors"
                          title="Delete Left Eye Image"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                      <img
                        src={`data:image/${previewImages.left.format};base64,${previewImages.left.base64_data}`}
                        alt="Left Eye"
                        className="w-full h-auto rounded-lg"
                      />
                      <div className="mt-2 text-xs text-gray-500 flex justify-between">
                        <span>{previewImages.left.width}x{previewImages.left.height}</span>
                        <span>{(previewImages.left.size_bytes / 1024).toFixed(1)} KB</span>
                        <span>{utcToLocal(previewImages.left.last_modified)}</span>
                        
                      </div>
                      
                    </div>
                  )}
                  
                </div>
              )}

              {/* Action Buttons */}

              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col gap-4">
            
            {/* Info Section - Top */}
            <div className="flex flex-wrap items-center gap-6 text-sm pb-4 border-b border-gray-200">
              
              {/* Fundus Images */}
              <div className="flex items-start" id="TimeStamp">
                    <span className='small fw-normal text-muted w-full flex items-center justify-center px-6 py-3 rounded-lg bg-pink-100 text-pink-800'>Connecting to server, please wait.</span>
              </div>

              

              {/* Error Message */}
              {reportError && (
                <>
                  <span className="text-gray-300">·</span>
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                    <span className="font-semibold text-red-800">Error:</span>
                    <span className="text-red-700">{reportError}</span>
                  </div>
                </>
              )}

              {/* Success Message */}
              {reportSuccess && (
                <>
                  <span className="text-gray-300">·</span>
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-md px-3 py-2.5">
                    <span className="font-semibold text-green-800">Success:</span>
                    <span className="text-green-700">{reportSuccess}</span>
                  </div>
                </>
              )}
            </div>

            {/* Report Action Buttons & Language */}
            <div className="flex flex-wrap items-center gap-2">
              {/* <button
                      onClick={() => handleViewReport('normal')}
                       disabled={isReportLoading}
                      className={`bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 ${isReportLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isReportLoading ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          Generating...
                        </>
                      ) : 'Standard Report'}
                    </button> */}
<button
                      onClick={() => handleViewReport('advanced')}
                       disabled={isReportLoading}
                      className={`bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 ${isReportLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isReportLoading ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          Generating...
                        </>
                      ) : 'Generate Patient Report'}
                    </button>

                    <button
                      onClick={handleSendReport}
                      disabled={!hasViewedReport || isSendingReport}
                      title={!hasViewedReport ? 'View the report at least once to enable this' : 'Send report to patient'}
                      className={`px-6 py-3 rounded-lg flex items-center gap-2 transition ${hasViewedReport && !isSendingReport ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                    >
                      {isSendingReport ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          Send Report to Patient
                        </>
                      )}
                    </button>

                    {/* <button
                      onClick={() => handleViewReport('tga')}
                       disabled={isReportLoading}
                      className={`bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 ${isReportLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isReportLoading ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          Generating...
                        </>
                      ) : 'Eye Conditions Report'}
                    </button> */}

              

            
              
              {/* Language Selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Language:</span>
                <div className="relative">
                  <select 
                    value={reportLanguage} 
                    onChange={(e) => setReportLanguage(e.target.value)}
                    className="appearance-none bg-gray-100 border border-gray-200 rounded-md px-4 py-2 pr-8 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    <option value="en">English</option>
                    <option value="my">Malay</option>
                    <option value="cn">Chinese</option>
                    <option value="th">Thai</option>
                  </select>
                  <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>

              
            </div>
          </div>
        </div>
        


              {/* <div className="flex gap-4 mb-6">
                <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                    
                      <button
                      onClick={() => handleViewReport('normal')}
                       disabled={isReportLoading}
                      className={`bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 ${isReportLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isReportLoading ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          Generating...
                        </>
                      ) : 'Standard Report'}
                    </button>
                    <button
                      onClick={() => handleViewReport('advanced')}
                      disabled={isReportLoading}
                      className={`bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 ${isReportLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isReportLoading ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          Generating...
                        </>
                      ) : 'Advanced Report'}
                    </button>
                    <button
                      onClick={() => handleViewReport('tga')}
                      disabled={isReportLoading}
                      className={`bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 ${isReportLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isReportLoading ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          Generating...
                        </>
                      ) : 'Eye Conditions Report'}
                    </button>

                  </div>
                  <div className="flex items-center gap-2"> 
                  <select
                    value={reportLanguage}
                    onChange={(e) => setReportLanguage(e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  >
                    <option value="en">English</option>
                    <option value="my">Malay</option>
                    <option value="cn">Chinese</option>
                    <option value="th">Thai</option>
                    
                  </select></div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-start" id="TimeStamp">
                    <span className='small fw-normal text-muted w-full flex items-center justify-center px-6 py-3 rounded-lg bg-pink-100 text-pink-800'><blink>Connecting to server, please wait.</blink></span>
                  </div>
                  {reportError && 
                      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded relative" role="alert">
                      <strong className="font-bold">Error: </strong>
                      <span className="block sm:inline">{reportError}</span>
                    </div>
                    }
                </div>
                
              </div> */}

          {/* <a
            href="/Disease_Explanation.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className=" bg-green-100 text-green-700 px-6 py-3 rounded-lg flex items-center gap-2 mb-8 hover:bg-green-200 transition"
          >
            <Printer size={20} />
            View Disease Explanation
          </a> */}
          
          <button className="bg-green-100 text-green-700 px-6 py-3 rounded-lg flex items-center gap-2 mt-5 mb-5 hover:bg-green-200 transition">
            <Printer size={20} />
            <a href="/Disease_Explanation.pdf" target="_blank" rel="noopener noreferrer">View Disease Explanation</a>
          </button>
           </>
          )}
          {/* Consent Checkbox */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-1 w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
              />
              <span className="text-gray-700">
                The patient has been informed and has provided consent for the collection and use of their retinal images for research and development purposes, as described in the{' '}
                <a href="#" className="text-indigo-600 hover:underline">[Terms of Use]</a>.
              </span>
            </label>
          </div>
        </>
      )}

      {/* Particulars Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Particulars</h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Success: </strong>
            <span className="block sm:inline">{successMessage}</span>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
            <input
              type="text"
              value={formData.patientName}
              onChange={(e) => setFormData({...formData, patientName: e.target.value})}
              autoComplete="off"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
            <select
              value={formData.gender}
              onChange={(e) => setFormData({...formData, gender: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="U">Unknown</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
              <option value="O">Other</option>
              <option value="N">Prefer not to say</option>

            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Patient ID</label>
            <input
              type="text"
              value={formData.patientId}
              onChange={(e) => setFormData({...formData, patientId: e.target.value})}
              autoComplete="off"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Ethnicity</label>
            <select
              value={formData.ethnicity}
              onChange={(e) => setFormData({...formData, ethnicity: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
                <option >Unknown</option>
                <option >Chinese</option>
                <option >Malay</option>
                <option >Indian / South Asian</option>
                <option value="Arab/Middle Eastern">Arab / Middle Eastern</option>
                <option >Black / African</option>
                <option >East Asian</option>
                <option >Southeast Asian</option>
                <option >White</option>
                <option >Hispanic / Latino</option>
                <option >Mixed / Multi-ethnic</option>
                <option >Other</option>
                <option >Prefer not to say</option>

            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">DOB (YYYY-MM-DD)</label>
            <input
              type="text"
              value={formData.dob}
              onChange={(e) => setFormData({...formData, dob: e.target.value})}
              placeholder="2000-01-01"
              autoComplete="off"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="patient@example.com"
              autoComplete="off"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Visual Symptoms / Concerns</label>
            <div className="flex flex-wrap gap-4">
              {[
                { key: 'visualSymptoms', label: 'Visual symptoms' },
                { key: 'recentChangeInVision', label: 'Recent change in vision' },
                { key: 'ocularDiscomfort', label: 'Ocular discomfort' },
                { key: 'otherVisualOrOcularConcern', label: 'Other visual or ocular concern' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={visualSymptoms[key]}
                    onChange={(e) => setVisualSymptoms(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                  />
                  <span className="text-gray-700 text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Self-declared Medical Conditions</label>
            <p className="text-sm text-gray-600 mb-3">Please indicate whether you have been diagnosed with or currently have any of the following conditions.</p>
            <div className="space-y-2">
              {[
                { key: 'diabetes', label: 'Diabetes' },
                { key: 'hypertension', label: 'Hypertension' },
                { key: 'highCholesterol', label: 'High cholesterol' },
              ].map(({ key, label }) => (
                <div key={key} className="flex flex-wrap items-center gap-4">
                  <span className="text-gray-700 text-sm w-44">{label}</span>
                  {[
                    { value: 'yes', text: 'Yes' },
                    { value: 'no', text: 'No' },
                    { value: 'unsure', text: 'Not sure' },
                  ].map(({ value, text }) => (
                    <label key={value} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name={key}
                        value={value}
                        checked={formData[key] === value}
                        onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-gray-700 text-sm">{text}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mt-8">
          <button
            onClick={handleSave}
            className="bg-indigo-600 text-white px-8 py-3 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
          >
            <span className="text-xl">✓</span>
            Save
          </button>
          <button
            onClick={handleBack}
            className="bg-indigo-600 text-white px-8 py-3 rounded-lg hover:bg-indigo-700 transition"
          >
            Back
          </button>
        </div>

        {/* Generate Referral Letter - shown after save if any symptom is selected */}
        {selectedPatient && savedSymptoms && Object.values(savedSymptoms).some(v => v) && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="mb-3 text-sm text-gray-600 space-y-1">
              <p>Click the <b>Generate Patient Report</b> button above to generate a patient report containing the retinal image analysis results and, where applicable, a referral letter based on the patient’s reported symptoms.</p>
              <p>Click the <b>Standalone Referral Letter</b> button below to generate a referral letter based solely on the patient’s reported symptoms, without retinal image analysis.</p>
            </div>
            <button
              onClick={handleGenerateReferralLetter}
              className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition flex items-center gap-2"
            >
              Standalone Referral Letter
            </button>
          </div>
        )}
      </div>

      {/* Symptoms History Table */}
      {symptomsHistory.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Symptoms History</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Date</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Session ID</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-700">Visual Symptoms</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-700">Recent Change in Vision</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-700">Ocular Discomfort</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-700">Other Concern</th>
                </tr>
              </thead>
              <tbody>
                {symptomsHistory.map((s) => (
                  <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{s.createdAt}</td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{s.sessionId}</td>
                    <td className="px-4 py-3 text-center">{s.visualSymptoms ? <span className="text-green-600 font-bold">✓</span> : <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-3 text-center">{s.recentChangeInVision ? <span className="text-green-600 font-bold">✓</span> : <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-3 text-center">{s.ocularDiscomfort ? <span className="text-green-600 font-bold">✓</span> : <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-3 text-center">{s.otherVisualOrOcularConcern ? <span className="text-green-600 font-bold">✓</span> : <span className="text-gray-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientManagementApp;
